/**
 * "Criar minha conta" — cadastro público (CERES-008b; refeito na CERES-ACC-005).
 *
 * Só a identidade: nome, e-mail, senha e aceite dos termos vigentes. A empresa
 * vem DEPOIS da confirmação do e-mail. A resposta do envio é a MESMA exista ou
 * não o e-mail (anti-enumeração) — a tela diz isso do jeito honesto: "se
 * estiver tudo certo, chega um e-mail".
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SimboloCeres } from '../components/Marca';
import { RodapePublico } from '../components/RodapePublico';
import { api } from '../lib/api';
import { idiomaDaTela, t } from '../lib/i18n';
import { useConfiguracaoPublica } from '../lib/configuracao-publica';
import { Field, FormErrorSummary, InlineError, Spinner } from '../components/ui';

interface RespostaDeCadastro {
  message: string;
  emailDelivered: boolean;
  emailFailed: boolean;
  /** Presente apenas em ambiente sem entrega de e-mail (desenvolvimento). */
  confirmationLink: string | null;
}

interface DocumentoExigido {
  chave: string;
  titulo: string;
  versao: string;
  hash: string;
}

/** Os documentos que exigem aceite, com o hash do texto que a pessoa pode abrir. */
function useDocumentosExigidos(): { documentos: DocumentoExigido[] | null; falhou: boolean } {
  const [documentos, setDocumentos] = useState<DocumentoExigido[] | null>(null);
  const [falhou, setFalhou] = useState(false);
  useEffect(() => {
    let vivo = true;
    const idioma = idiomaDaTela();
    void (async () => {
      try {
        const lista = await api.get<{ data: { chave: string; exigeAceite: boolean }[] }>(
          `/legal/documentos?idioma=${idioma}`,
        );
        const exigidos = await Promise.all(
          lista.data
            .filter((d) => d.exigeAceite)
            .map((d) =>
              api.get<DocumentoExigido>(
                `/legal/documentos/${encodeURIComponent(d.chave)}?idioma=${idioma}`,
              ),
            ),
        );
        if (vivo)
          setDocumentos(
            exigidos.map(({ chave, titulo, versao, hash }) => ({ chave, titulo, versao, hash })),
          );
      } catch {
        if (vivo) setFalhou(true);
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);
  return { documentos, falhou };
}

export function CriarContaPage(): JSX.Element {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [aceito, setAceito] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [enviado, setEnviado] = useState<RespostaDeCadastro | null>(null);
  const [reenviado, setReenviado] = useState(false);
  const { config: configPublica, carregando: carregandoConfig } = useConfiguracaoPublica();
  const { documentos, falhou: termosFalharam } = useDocumentosExigidos();

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setAviso(null);
    if (senha !== confirmacao) {
      setAviso(t('cadastro.senhasDiferentes'));
      return;
    }
    if (!aceito || documentos === null) {
      setAviso(t('cadastro.aceiteObrigatorio'));
      return;
    }
    setBusy(true);
    try {
      const resposta = await api.post<RespostaDeCadastro>('/auth/cadastro', {
        nome,
        email,
        senha,
        aceites: documentos.map(({ chave, versao, hash }) => ({ chave, versao, hash })),
        idioma: idiomaDaTela(),
      });
      setEnviado(resposta);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const reenviar = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      const resposta = await api.post<RespostaDeCadastro>('/auth/cadastro/reenviar', { email });
      setEnviado(resposta);
      setReenviado(true);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-sunken px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <SimboloCeres className="mb-3 h-11 w-11 text-brand-600" decorativa />
          <h1 className="text-xl font-semibold text-ink">{t('cadastro.titulo')}</h1>
          <p className="mt-0.5 text-sm text-ink-muted">{t('cadastro.subtitulo')}</p>
        </div>

        {!carregandoConfig && !configPublica.cadastroPublicoHabilitado ? (
          /*
           * CERES-EST-001 §11 — o endereço continua existindo e responde com
           * honestidade. Quem chegou por um link antigo lê o que fazer, em vez
           * de preencher o formulário para receber um 403 no fim.
           */
          <div className="panel px-5 py-5 text-center">
            <p role="status" className="text-sm font-medium text-ink">
              {t('cadastro.indisponivelTitulo')}
            </p>
            <p className="mt-2 text-sm text-ink-muted">{t('cadastro.indisponivelTexto')}</p>
            <Link to="/entrar" className="btn-secondary btn-sm mt-4 inline-flex">
              {t('cadastro.voltarEntrar')}
            </Link>
          </div>
        ) : enviado !== null ? (
          <div className="panel px-5 py-5 text-center">
            <p role="status" className="text-sm font-medium text-ink">
              {t('cadastro.enviadoTitulo')}
            </p>
            <p className="mt-2 text-sm text-ink-muted">{t('cadastro.enviadoTexto')}</p>
            {reenviado && <p className="mt-2 text-xs text-ink-muted">{t('cadastro.reenviado')}</p>}
            {/* Só em desenvolvimento, quando não há entrega real de e-mail. */}
            {enviado.confirmationLink !== null && (
              <p className="mt-3 break-all rounded border border-line bg-surface-sunken p-2 text-2xs text-ink-subtle">
                <a href={enviado.confirmationLink} className="underline">
                  {enviado.confirmationLink}
                </a>
              </p>
            )}
            <InlineError error={error} />
            <div className="mt-4 flex flex-col gap-2">
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={busy}
                onClick={() => void reenviar()}
              >
                {busy && <Spinner />}
                {t('cadastro.reenviar')}
              </button>
              <Link
                to="/criar-conta/empresa"
                className="text-xs underline text-ink-muted hover:text-ink"
              >
                {t('cadastro.jaConfirmei')}
              </Link>
            </div>
          </div>
        ) : (
          <div className="panel px-5 py-5">
            <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4" noValidate>
              <Field label={t('cadastro.nome')} htmlFor="nome" required>
                <input
                  id="nome"
                  type="text"
                  autoComplete="name"
                  required
                  autoFocus
                  className="field-input"
                  value={nome}
                  onChange={(event) => setNome(event.target.value)}
                />
              </Field>

              <Field label={t('cadastro.email')} htmlFor="email" required>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="field-input"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Field>

              <Field
                label={t('cadastro.senha')}
                htmlFor="senha"
                required
                hint={t('cadastro.senhaAjuda')}
              >
                <input
                  id="senha"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="field-input"
                  value={senha}
                  onChange={(event) => setSenha(event.target.value)}
                />
              </Field>

              <Field label={t('cadastro.confirmarSenha')} htmlFor="confirmacao" required>
                <input
                  id="confirmacao"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="field-input"
                  value={confirmacao}
                  onChange={(event) => setConfirmacao(event.target.value)}
                />
              </Field>

              {termosFalharam ? (
                <p role="alert" className="text-xs text-danger-700">
                  {t('cadastro.aceiteIndisponivel')}
                </p>
              ) : (
                <label className="flex items-start gap-2 text-xs text-ink-muted">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={aceito}
                    disabled={documentos === null}
                    onChange={(event) => setAceito(event.target.checked)}
                  />
                  <span>
                    {t('cadastro.aceitePrefixo')}{' '}
                    {(documentos ?? []).map((d, i) => (
                      <span key={d.chave}>
                        {i > 0 && ', '}
                        <Link
                          to={`/legal/${d.chave}`}
                          target="_blank"
                          className="underline hover:text-ink"
                        >
                          {d.titulo}
                        </Link>
                      </span>
                    ))}
                  </span>
                </label>
              )}

              {aviso !== null && (
                <p role="alert" className="text-xs text-danger-700">
                  {aviso}
                </p>
              )}
              <FormErrorSummary error={error} />
              <InlineError error={error} />

              <button
                type="submit"
                className="btn-primary w-full"
                disabled={busy || documentos === null}
              >
                {busy && <Spinner />}
                {t('cadastro.criar')}
              </button>
            </form>
          </div>
        )}

        <p className="mt-4 text-center text-sm text-ink-muted">
          {t('cadastro.jaTem')}{' '}
          <Link to="/entrar" className="underline hover:text-ink">
            {t('cadastro.entrar')}
          </Link>
        </p>

        <RodapePublico />
      </div>
    </div>
  );
}
